import { type Column } from "@tanstack/react-table";
import { ArrowDown, ArrowUp, ChevronsUpDown, EyeOff } from "lucide-react";

import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Text,
} from "@/components/ui";
import { cn } from "@/lib/utils/cn";
import { formatTableColumnLabel } from "./formatTableColumnLabel";
import type { DataTableColumnMeta } from "./DataTable";

interface DataTableColumnHeaderProps<TData, TValue>
  extends React.HTMLAttributes<HTMLDivElement> {
  column: Column<TData, TValue>;
}

export function DataTableColumnHeader<TData, TValue>({
  column,
  className,
}: DataTableColumnHeaderProps<TData, TValue>) {
  const canHide = column.getCanHide();
  const meta = column.columnDef.meta as DataTableColumnMeta | undefined;
  const label = formatTableColumnLabel(column.id, meta?.preserveHeaderLabel);

  const labelContent = (
    <Text
      as="span"
      variant={canHide ? "subHeading" : "body"}
      className={cn(
        "text-nowrap break-normal whitespace-nowrap",
        !canHide && "font-semibold",
      )}
    >
      {label}
    </Text>
  );

  if (!column.getCanSort() && !column.getCanHide()) {
    return (
      <div
        className={cn(
          "flex items-center space-x-2 whitespace-nowrap",
          className,
        )}
      >
        {labelContent}
      </div>
    );
  }

  return (
    <div
      className={cn("flex items-center space-x-2 whitespace-nowrap", className)}
    >
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="data-[state=open]:bg-primary/25 hover:bg-primary/25 h-8 max-w-none whitespace-nowrap"
            data-testid={`data-table-column-sort-${column.id}`}
          >
            {labelContent}

            {column.getIsSorted() === "desc" ? (
              <ArrowDown />
            ) : column.getIsSorted() === "asc" ? (
              <ArrowUp />
            ) : (
              <ChevronsUpDown />
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          {column.getCanSort() && (
            <DropdownMenuItem
              onClick={() => {
                column.toggleSorting(false);
              }}
              data-testid={`data-table-column-sort-${column.id}-asc`}
            >
              <ArrowUp className="text-muted-foreground/70 h-3.5 w-3.5" />
              Asc
            </DropdownMenuItem>
          )}
          {column.getCanSort() && (
            <DropdownMenuItem
              onClick={() => {
                column.toggleSorting(true);
              }}
              data-testid={`data-table-column-sort-${column.id}-desc`}
            >
              <ArrowDown className="text-muted-foreground/70 h-3.5 w-3.5" />
              Desc
            </DropdownMenuItem>
          )}
          {column.getCanSort() && column.getCanHide() && (
            <DropdownMenuSeparator />
          )}
          {column.getCanHide() && (
            <DropdownMenuItem
              onClick={() => {
                column.toggleVisibility(false);
              }}
              data-testid={`data-table-column-sort-${column.id}-hide`}
            >
              <EyeOff className="text-muted-foreground/70 h-3.5 w-3.5" />
              Hide
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
